import { useSelector } from 'react-redux'
import { ProjectWithComments } from '../../../../types/project'
import { isMinimumPackage } from '../../../../utils/package-validator'
import { Package } from '../../../../enums/package.enum'
import { State } from '../../../../types/state'

interface MoodboardViewProps {
  project: ProjectWithComments
  openEditModal: (itemId: string) => void
  handleApproveItem: (itemId: string) => void
  handleRejectItem: (itemId: string) => void
  handleCopyItem: (itemId: string) => void
  handleDeleteItem: (itemId: string) => void
  globalCategoryFilter?: string
  globalStatusFilter?: string[]
  onOpenImageModal: (itemId: string, imageUrl?: string) => void
  globalStageFilter?: string
}

export const MoodboardView = ({
  project,
  openEditModal,
  handleApproveItem,
  handleRejectItem,
  handleCopyItem,
  handleDeleteItem,
  globalCategoryFilter,
  globalStatusFilter,
  onOpenImageModal,
  globalStageFilter
}: MoodboardViewProps) => {
  const { package: userPackage, packageExpiredAt } = useSelector((state: State) => state.user)

  if ((project == null) || (project.items.length === 0)) {
    return (
      <div className='tw-p-4 tw-bg-sky-100 tw-text-sky-700 tw-rounded-lg tw-text-center'>
        Brak elementów w projekcie. Dodaj elementy, aby zobaczyć je na tablicy inspiracji.
      </div>
    )
  }

  return (
    <div className='tw-space-y-10 tw-py-10'>
      {(globalStageFilter
        ? project.stages.filter(stage => stage.name === globalStageFilter)
        : project.stages
      ).map(stage => {
        let stageItems = project.items.filter(item => item.stageId === stage.id)
        if (globalCategoryFilter) stageItems = stageItems.filter(item => item.category === globalCategoryFilter)
        if (stageItems.length === 0) return null
        return (
          <div key={stage.id} className='tw-mb-8'>
            <h5 className='tw-text-xl tw-font-semibold tw-mb-4'>{stage.name}</h5>
            <div className='tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4 tw-gap-6'>
              {stageItems.map(item => (
                <div
                  key={item.id}
                  className='tw-group tw-bg-white tw-shadow-lg hover:tw-shadow-xl tw-rounded-lg tw-overflow-hidden tw-flex tw-flex-col tw-h-full tw-transition-shadow tw-duration-300 ease-in-out'
                >
                  <div
                    className='tw-h-[200px] tw-bg-gray-100 tw-flex tw-justify-center tw-items-center tw-overflow-hidden tw-cursor-pointer'
                    onClick={() => onOpenImageModal(item.id, item.imageUrl)}
                  >
                    {item.imageUrl
                        ? (
                            <img
                                src={item.imageUrl}
                                alt={item.name}
                                className='tw-max-h-full tw-max-w-full tw-object-contain group-hover:tw-scale-110 tw-transition-transform tw-duration-300 ease-in-out'
                                onError={(e) => {
                                    (e.currentTarget as HTMLImageElement).src = 'https://via.placeholder.com/200x200?text=Brak+zdj%C4%99cia'
                                  }}
                              />
                          )
                        : (
                            <i className='fa fa-image tw-text-5xl tw-text-gray-400' />
                          )}
                  </div>
                  <div className='tw-p-4 tw-flex tw-flex-col tw-flex-grow'>
                    <h5 className='tw-text-lg tw-font-semibold tw-mb-1'>{item.name}</h5>
                    <div className='tw-text-sm tw-text-gray-600 tw-mb-3'>
                        {item.dimensions && (
                            <small className='tw-block tw-text-xs tw-mb-0.5'>{item.dimensions}</small>
                          )}
                        <span className='tw-text-xs tw-font-semibold tw-px-2.5 tw-py-0.5 tw-rounded-full tw-bg-blue-100 tw-text-blue-800 tw-mr-2'>
                            {item.price.toFixed(2)} PLN
                          </span>
                        <span className='tw-text-xs tw-font-semibold tw-px-2.5 tw-py-0.5 tw-rounded-full tw-bg-gray-100 tw-text-gray-800'>
                            {item.quantity} {item.unit}
                          </span>
                      </div>
                    {isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt) && <div className='tw-mb-2'>
                        {item.status === 'approved' && (
                            <span className='tw-inline-block tw-text-xs tw-font-semibold tw-px-2.5 tw-py-0.5 tw-rounded-full tw-bg-green-100 tw-text-green-800'>
                                Zaakceptowany
                            </span>
                          )}
                        {item.status === 'rejected' && (
                            <span className='tw-inline-block tw-text-xs tw-font-semibold tw-px-2.5 tw-py-0.5 tw-rounded-full tw-bg-red-100 tw-text-red-800'>
                                Odrzucony
                            </span>
                          )}
                        {(!item.status || item.status === 'pending') && (
                            <span className='tw-inline-block tw-text-xs tw-font-semibold tw-px-2.5 tw-py-0.5 tw-rounded-full tw-bg-yellow-100 tw-text-yellow-800'>
                                Oczekujący
                            </span>
                          )}
                                                                                        </div>}
                    <div className='tw-mt-auto tw-pt-2'>
                        <span className='tw-text-xs tw-font-semibold tw-px-2.5 tw-py-0.5 tw-rounded-full tw-bg-sky-100 tw-text-sky-800'>
                            {stage.name}
                          </span>
                      </div>
                  </div>
                  <div className='tw-p-3 tw-bg-white tw-border-t tw-border-gray-200'>
                    <div className='tw-w-full tw-flex tw-items-center tw-justify-between tw-gap-1'>
                        {[
                            { action: () => openEditModal(item.id), title: 'Edytuj element', icon: 'fa-edit', color: 'tw-text-blue-600', ring: 'focus:tw-ring-blue-400' },
                            ...(isMinimumPackage(userPackage, Package.PLUS, packageExpiredAt)
                              ? [
                                  { action: async () => handleCopyItem(item.id), title: 'Kopiuj element', icon: 'fa-copy', color: 'tw-text-gray-500', ring: 'focus:tw-ring-gray-400' }]
                              : []),
                            { action: async () => handleDeleteItem(item.id), title: 'Usuń element', icon: 'fa-trash', color: 'tw-text-gray-700 hover:tw-text-red-600', ring: 'focus:tw-ring-gray-400 hover:focus:tw-ring-red-400' }
                          ].map(btn => (
                              <button
                                  key={btn.title}
                                  className={`tw-p-1.5 tw-rounded-md hover:tw-bg-gray-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-offset-1 ${btn.color} ${btn.ring} tw-transition-colors tw-duration-150`}
                                  onClick={btn.action}
                                  title={btn.title}
                                >
                                  <i className={`fa ${btn.icon}`} />
                                </button>
                          ))}
                      </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}
